import { number, arrayOf, string, shape, func } from 'prop-types';
import React, { useState } from 'react';
import { Mutation } from 'react-apollo';

import withUserAuthentication from 'lib/withUserAuthentication';
import Modal from 'components/Modal';
import Teaser from 'components/Teaser';
import RecipeRating from 'components/RecipeRating';
import ManageRecipeTagsModal from 'components/RecipeBox/ManageRecipeTagsModal';
import GetUserRecipeTags from 'components/GetUserRecipeTags';
import RecipeStar from 'components/RecipeStar';
import printCleanCookTime from 'util/recipes';
import { getLinkProps } from 'util/link';
import { isMobileDevice } from 'util/viewport';
import Error from 'components/Error';
import IconDelete from 'components/Icons/Close';
import IconEdit from 'components/Icons/IconPencil';
import IconArrow from 'components/Icons/Arrow';
import { GET_USER_RECIPE_BOX } from 'graphql/recipebox';
import { GET_USER_DATA_SUMMARY } from 'graphql/userDataSummary';
import { MUTATION } from 'components/RecipeRatingsDataProvider';
import { getFormattedDate } from 'oxygen-lib-ui/helpers/date';

import styles from './styles';

export function TeaserFlipCard(props) {
  const {
    headline,
    url,
    recipeId,
    userRecipeId,
    userTags,
    userTagsIds,
    userNotes,
    ratingData,
    onRecipeBoxDataChange,
    onCreateUserRecipeTags,
    onUpdateRecipeInBox,
    onRemoveRecipeFromBox,
    prepTimeMin,
    prepTimeMax,
    cookTimeMin,
    cookTimeMax,
    user
  } = props;

  const [notes, setNotes] = useState(userNotes);
  const [hasShowEditTagsModal, setHasShowEditTagsModal] = useState(false);
  const [hasShowEditMobileForm, setHasShowEditMobileForm] = useState(false);
  const [error, setError] = useState(null);
  const [isUpdating, setIsUpdating] = useState(false);
  // Flag used to do an optimistic deletion by hidding the grid item
  const [isDeleted, setIsDeleted] = useState(false);

  const toggleEditTagsModal = () =>
    setHasShowEditTagsModal(!hasShowEditTagsModal);

  const toggleEditMobileForm = () =>
    setHasShowEditMobileForm(!hasShowEditMobileForm);

  const handleNotesChange = e => {
    e.preventDefault();
    setNotes(e.target.value);
  };

  const handleFormSubmit = e => {
    e.preventDefault();
    setIsUpdating(true);
    setError(null);

    const updateCache = (cache, { data: { updateRecipeInBox } }) => {
      try {
        const rb = cache.readQuery({
          query: GET_USER_RECIPE_BOX
        });
        cache.writeQuery({
          query: GET_USER_RECIPE_BOX,
          data: {
            recipeBox: {
              ...rb.recipeBox,
              recipes: [
                ...rb.recipeBox.recipes.filter(
                  r => r.userRecipeId.toString() !== userRecipeId.toString()
                ),
                updateRecipeInBox.userRecipe
              ]
            }
          }
        });
      } catch (err) {
        // nothing to do here, sadly.
      }
    };
    onUpdateRecipeInBox({
      variables: {
        input: {
          userRecipeId,
          tagsIds: userTagsIds,
          notes: notes.trim()
        }
      },
      update: updateCache
    })
      .then((_, err) => {
        if (err) {
          setError(err);
          setIsUpdating(false);
          return;
        }
        setIsUpdating(false);
      })
      .catch(err => {
        setError(err);
        setIsUpdating(false);
      });
  };

  const handleRecipeRemoval = () => {
    if (
      // eslint-disable-next-line
      window.confirm(
        'Are you sure you want to remove this Recipe from your Box?'
      )
    ) {
      setIsUpdating(true);
      setError(null);

      const updateCache = (
        cache,
        {
          data: {
            removeRecipeFromBox: { status }
          }
        }
      ) => {
        if (status !== 200) {
          // eslint-disable-next-line no-console
          console.log(`Error deleting recipe from the box ${userRecipeId}`);
          return;
        }
        try {
          const rb = cache.readQuery({
            query: GET_USER_RECIPE_BOX
          });
          cache.writeQuery({
            query: GET_USER_RECIPE_BOX,
            data: {
              recipeBox: {
                ...rb.recipeBox,
                // Remove the deleted recipe
                recipes: rb.recipeBox.recipes.filter(
                  r => r.userRecipeId !== userRecipeId
                )
              }
            }
          });
        } catch (err) {
          // nothing to do here, sadly.
        }
      };
      onRemoveRecipeFromBox({
        variables: {
          input: { userRecipeId }
        },
        update: updateCache
      })
        .then((_, err) => {
          if (err) {
            setError(err);
            setIsUpdating(false);
            return;
          }
          // Mark this teaser as deleted so we can hide it on the UI
          setIsDeleted(true);
          // Callback to ask Algolia's client to clear the cache
          onRecipeBoxDataChange();
          setIsUpdating(false);
          if (isMobileDevice()) {
            toggleEditMobileForm();
          }
        })
        .catch(err => {
          setIsUpdating(false);
          // eslint-disable-next-line no-alert
          window.alert(
            `There was a problem deleting this Recipe from the Box: ${err.message}`
          );
        });
    }
  };

  const gtmAttrs = {
    'data-gtm-recipebox-user': user.auth0User,
    'data-gtm-recipebox-recipe-url': props.url
  };

  const recipeTime = printCleanCookTime(
    prepTimeMin,
    prepTimeMax,
    cookTimeMin,
    cookTimeMax
  );

  const teaserProps = {
    ...props,
    variant: 'recipeBoxSearchResult',
    fallbackDims: {
      height: 200,
      width: 300
    },
    sourceSets: [
      {
        imageDims: {
          height: 405.5,
          width: 608
        },
        layoutWidth: {
          units: 'vw',
          value: 39
        },
        media: '(min-width: 64rem)'
      },
      {
        imageDims: {
          height: 405.5,
          width: 608
        },
        layoutWidth: {
          units: 'vw',
          value: 100
        },
        media: '(max-width: 63.99rem)'
      }
    ],
    gtmAttrs,
    dek: recipeTime,
    kicker: userTags.join(' | '),
    placeholders: [
      {
        media: '(min-width: 0)',
        ratio: 1.5
      }
    ]
  };

  const renderEditForm = () => (
    <div className="TeaserFlipCard__backInfo">
      <div className="TeaserFlipCard__backInfoToolbar">
        <button
          className="TeaserFlipCard__backInfoIconButton"
          onClick={handleRecipeRemoval}
        >
          <IconDelete />
          &nbsp;Remove
        </button>
      </div>
      <h3>{headline}</h3>
      <div className="TeaserFlipCard__backInfoOrganize">
        <button
          className="TeaserFlipCard__backInfoIconButton"
          onClick={() => toggleEditTagsModal()}
        >
          Organize&nbsp;
          <IconEdit />
        </button>
        <span>{userTags.join(' | ')}</span>
      </div>
      <div className="TeaserFlipCard__backInfoRating">
        <Mutation mutation={MUTATION}>
          {(mutate, { loading: rateRecipeIsLoading }) => {
            // mutate returns a promise so you can do rateRecipe().then()
            const rateRecipe = userRating =>
              mutate({
                variables: {
                  recipeId: parseInt(recipeId, 10),
                  userRating
                },
                update: (cache, { data: { rate } }) => {
                  try {
                    const uds = cache.readQuery({
                      query: GET_USER_DATA_SUMMARY
                    });
                    cache.writeQuery({
                      query: GET_USER_DATA_SUMMARY,
                      data: {
                        getUserDataSummary: {
                          ...uds.getUserDataSummary,
                          ratedResources: [
                            ...uds.getUserDataSummary.ratedResources.filter(
                              r => r.ratedId.toString() !== recipeId.toString()
                            ),
                            rate.rating
                          ]
                        }
                      }
                    });
                  } catch (err) {
                    // nothing to do here, sadly.
                  }
                }
              });
            return (
              <RecipeRating
                ratingsData={
                  ratingData
                    ? { totalRatings: 1, average: ratingData.rating }
                    : { totalRatings: 0, average: 0 }
                }
                rateRecipe={rateRecipe}
                rateRecipeIsLoading={rateRecipeIsLoading}
                recipeId={parseInt(recipeId, 10)}
                fetchUserRating={() =>
                  Promise.resolve({
                    data: ratingData
                      ? {
                          getUserRating: ratingData
                        }
                      : {}
                  })
                }
                placement="recipeBox"
              />
            );
          }}
        </Mutation>
      </div>
      <form
        className="TeaserFlipCard__backInfoNotes"
        onSubmit={handleFormSubmit}
      >
        <label htmlFor={`TeaserFlipCard__notesTextarea-${userRecipeId}`}>
          Notes
          <textarea
            id={`TeaserFlipCard__notesTextarea-${userRecipeId}`}
            name="notes"
            rows="3"
            value={notes}
            onChange={handleNotesChange}
          />
        </label>
        <button
          type="submit"
          disabled={notes === userNotes || notes.trim() === '' || isUpdating}
          required
        >
          Save Notes
        </button>
        {error && <Error message={error.message} />}
      </form>
      <a className="TeaserFlipCard__backInfoButton" href={getLinkProps(url).as}>
        Go to Recipe
      </a>
      <style jsx>{styles}</style>
    </div>
  );

  if (isDeleted) {
    return null;
  }

  return (
    <>
      <div className="TeaserFlipCard">
        <div className="TeaserFlipCard__flipBox">
          <div className="TeaserFlipCard__flipBoxFront">
            <div className="TeaserFlipCard__recipeFront">
              <Teaser {...teaserProps} tags={undefined} />
              <div className="TeaserFlipCard__recipeFrontInfo">
                <div className="TeaserFlipCard__recipeFrontRating">
                  <ul className="TeaserFlipCard__recipeFrontRatingStars">
                    {new Array(5).fill(null).map((_, i) => {
                      const pos = i + 1;
                      return (
                        <li
                          key={pos}
                          className="TeaserFlipCard__recipeFrontRatingStar"
                        >
                          <RecipeStar
                            rating={ratingData ? ratingData.rating : 5}
                            pos={pos}
                            fillColor={ratingData ? '#ff881f' : '#d6d1d1'}
                          />
                        </li>
                      );
                    })}
                  </ul>
                  <span className="TeaserFlipCard__recipeFrontRatingLabel">
                    {ratingData
                      ? `Rated ${getFormattedDate(
                          ratingData.updatedAt || ratingData.createdAt,
                          '%b %d, %Y'
                        )}`
                      : 'Not Rated'}
                  </span>
                </div>
              </div>
              {isMobileDevice() && (
                <div className="TeaserFlipCard__showEditMobileForm">
                  <a href="#" onClick={toggleEditMobileForm}>
                    <IconEdit />
                  </a>
                </div>
              )}
              {isUpdating && (
                <div className="TeaserFlipCard__isUpdatingMessageBox">
                  <span>Saving...</span>
                </div>
              )}
            </div>
          </div>
          <div className="TeaserFlipCard__flipBoxBack">
            {renderEditForm()}
            {isUpdating && (
              <div className="TeaserFlipCard__isUpdatingMessageBox">
                <span>Saving...</span>
              </div>
            )}
          </div>
        </div>
      </div>
      {isMobileDevice() && hasShowEditMobileForm && (
        <div className="TeaserFlipCard__editMobileForm">
          <div className="TeaserFlipCard__editMobileFormHeader">
            <a href="#" onClick={toggleEditMobileForm}>
              <IconArrow rotate={180} />
              <span>Back</span>
            </a>
          </div>
          {renderEditForm()}
        </div>
      )}
      {hasShowEditTagsModal && (
        <GetUserRecipeTags>
          {({ data: allUserTags }) => (
            <Modal
              onClose={toggleEditTagsModal}
              cssHook="ManageRecipeTagsModal"
            >
              <ManageRecipeTagsModal
                userRecipeId={userRecipeId}
                allUserTags={allUserTags.recipeTags || []}
                recipeTags={userTags}
                recipeTagsIds={userTagsIds}
                toggleDialog={toggleEditTagsModal}
                onRecipeBoxDataChange={onRecipeBoxDataChange}
                onUpdate={() => {
                  setIsUpdating(true);
                  setTimeout(() => setIsUpdating(false), 1500);
                }}
                onCreateUserRecipeTags={onCreateUserRecipeTags}
                onUpdateRecipeInBox={onUpdateRecipeInBox}
              />
            </Modal>
          )}
        </GetUserRecipeTags>
      )}
      <style jsx>{styles}</style>
    </>
  );
}

TeaserFlipCard.propTypes = {
  ...Teaser.propTypes,
  index: string,
  recipeId: string,
  userRecipeId: string,
  userTags: arrayOf(string),
  userTagsIds: arrayOf(string),
  userNotes: string,
  ratingData: shape({}),
  cookTimeMax: number,
  cookTimeMin: number,
  prepTimeMax: number,
  tags: arrayOf(string),
  prepTimeMin: number,
  user: shape({
    auth0User: string.isRequired
  }),
  onRecipeBoxDataChange: func.isRequired,
  onCreateUserRecipeTags: func.isRequired,
  onUpdateRecipeInBox: func.isRequired,
  onRemoveRecipeFromBox: func.isRequired
};

TeaserFlipCard.defaultProps = {
  ...Teaser.defaultProps,
  recipeId: null,
  userRecipeId: null,
  userTags: null,
  userNotes: null,
  ratingData: null,
  cookTimeMax: null,
  cookTimeMin: null,
  prepTimeMax: null,
  prepTimeMin: null,
  tags: [],
  user: undefined
};

export default withUserAuthentication(TeaserFlipCard);
